import { useEffect, useRef } from 'react';
import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { toURL } from '@eneco/common/path';
import { stripAccolades } from '@eneco/common/string';
import { Recaptcha, RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { useFormsPublicV2PostFormV2 } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Button, Heading, Stack, Text, NotificationBox, Card, RadioButton, RadioGroup } from '@eneco/sparky';
import type { RequestModels_Forms_V2_FormsRequestModelV2 } from '@monorepo-types/dc';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { hintOf } from './contact-form/hint';
import { ContactInputs } from './deceased-form/ContactInputs';
import { DeceasedInputs } from './deceased-form/DeceasedInputs';
import { getSchema } from './deceased-form/schema';
import type { FormValues } from './deceased-form/types';
import type { DeceasedFormContent } from './deceased-form-content.types';

/**
 * `DeceasedForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/forms/src/DeceasedForm/DeceasedForm.tsx` 1:1: the deceased's details,
 * the contact person's details and whether to change the payment method, posted to the forms v2
 * endpoint, then redirecting to the submit link; a failure shows the generic error notification. Drops
 * the legacy `wrap()`/Experience Editor `editable` checks — this lane never runs inside the Experience
 * Editor.
 *
 * Both adapters hand every field over under the same flat name. The inline rich texts are inline HTML
 * on Sitecore and one paragraph on Sanity, so they are unwrapped; a payment-method option has a
 * `value` only on Sitecore, equal to its label, which Sanity's options fall back to so the submitted
 * answer stays the same.
 */
const DeceasedForm: FC = () => {
  const { adapter } = useCms();
  const content = useContent<DeceasedFormContent>();
  const senderId = content.senderIdOption ?? '';
  const paymentMethodField = content.changePaymentMethodFormField;
  const notification = content.genericErrorNotification;

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const { send, isSuccess, isError } = useFormsPublicV2PostFormV2();
  const { label, businessUnit } = useDC();
  const { pushHref } = useRouter();

  const resolver = typedYupResolver<FormValues>(getSchema(content));
  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver,
  });
  const {
    control,
    register,
    formState: { errors, isSubmitting },
  } = methods;

  const submitForm: SubmitHandler<FormValues> = values => {
    const {
      changePaymentMethod,
      contactEmail,
      contactFirstName,
      contactHouseNumber,
      contactHouseNumberSuffix,
      contactPhoneNumber,
      contactPostalCode,
      contactSalutation,
      contactSurname,
      contactSurnamePreposition,
      customerId,
      deceasedDate,
      deceasedHouseNumber,
      deceasedHouseNumberSuffix,
      deceasedPostalCode,
      endContractDate,
      recaptchaToken,
    } = values;
    const requestBody: RequestModels_Forms_V2_FormsRequestModelV2 = {
      data: {
        destinationCode: senderId,
        attachments: [],
        city: '',
        emailAddress: '',
        houseNumber: 0,
        houseNumberSuffix: '',
        postalCode: '',
        street: '',
        initials: '',
        surnamePreposition: '',
        surname: '',
        urlOrigin: window.location.href,
        formFields: [
          { key: 'Adressering contactpersoon', value: contactSalutation },
          { key: 'Voornaam contactpersoon', value: contactFirstName },
          { key: 'Tussenvoegsel contactpersoon', value: contactSurnamePreposition },
          { key: 'Achternaam contactpersoon', value: contactSurname },
          { key: 'Emailadres contactpersoon', value: contactEmail },
          { key: 'Telefoonnummer contactpersoon', value: contactPhoneNumber },
          { key: 'Postcode contactpersoon', value: contactPostalCode },
          { key: 'Huisnummer contactpersoon', value: contactHouseNumber },
          { key: 'Huisnummertoevoeging contactpersoon', value: contactHouseNumberSuffix },
          { key: 'Telefoonnummer contactpersoon', value: contactPhoneNumber },
          { key: 'Postcode contactpersoon', value: contactPostalCode },
          { key: 'Postcode overledene', value: deceasedPostalCode },
          { key: 'Huisnummer overledene', value: deceasedHouseNumber },
          { key: 'Huisnummertoevoeging overledene', value: deceasedHouseNumberSuffix },
          { key: 'Klantnummer', value: customerId },
          { key: 'Verander betaalmethode', value: changePaymentMethod },
          { key: 'Datum overlijden', value: deceasedDate },
          { key: 'Datum stopzetten', value: endContractDate },
        ],
      },
    };
    send({
      label,
      formId: stripAccolades(senderId),
      businessUnit,
      requestBody,
      recaptchaEnterpriseToken: recaptchaToken,
    } as Parameters<typeof send>[0]);
  };

  const submitHref = linkWithUrl(content.submitButtonLink)?.href;
  useEffect(() => {
    if (!isSuccess) return;
    pushHref(toURL(submitHref ?? ''));
  }, [isSuccess, submitHref, pushHref]);

  return (
    <Card>
      <Card.Content>
        <FormProvider {...methods}>
          <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
            <Stack gap="6">
              <Heading as="h2" size="M">
                {content.header_title}
              </Heading>
              <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                <RichText inline html={adapter.toRichText(content.header_description)} />
              </Text>
              <DeceasedInputs content={content} register={register} errors={errors} control={control} />
              <ContactInputs content={content} register={register} errors={errors} control={control} />
              <Heading as="h3" size="S">
                {content.footer_title}
              </Heading>
              <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                <RichText inline html={adapter.toRichText(content.footer_description)} />
              </Text>
              <Controller
                control={control}
                name="changePaymentMethod"
                render={({ field: { onChange, value, name } }) => (
                  <RadioGroup
                    name={name}
                    value={value}
                    onValueChange={onChange}
                    label={paymentMethodField?.label ?? ''}
                    error={errors?.changePaymentMethod?.message}
                    hint={hintOf(paymentMethodField?.hint)}>
                    {paymentMethodField?.options?.map(option => {
                      const optionValue = option.value ?? option.label ?? '';
                      return (
                        <RadioButton value={optionValue} key={optionValue}>
                          {option.label ?? ''}
                        </RadioButton>
                      );
                    })}
                  </RadioGroup>
                )}
              />
              <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                <RichText inline html={adapter.toRichText(content.confirmDescription)} />
              </Text>
              <Text size="BodyS">
                <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
              </Text>
              {isError ? (
                <NotificationBox
                  isAlert
                  text={<RichText html={adapter.toRichText(notification?.content)} />}
                  title={notification?.title}
                  variant={notification?.variant}
                />
              ) : null}
              <Stack.Item grow={false}>
                <Button type="submit" isLoading={isSubmitting}>
                  {content.submitButtonLink?.text}
                </Button>
              </Stack.Item>
            </Stack>
          </RecaptchaForm>
        </FormProvider>
      </Card.Content>
    </Card>
  );
};

export default DeceasedForm;
